import {
  borgAnalytics,
  Button,
  getChannel,
  Input,
  onEnter,
  usePhoneNumberMask,
  useQueryParams,
  useTheme,
} from '@ytl/common-web';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../contexts/languageContext';
import { useOpenAm } from '../contexts/openAmContext';
import { OpenAmAuthenticateError } from '../shared/@types/OpenAmInitialData';
import { LoginView, StageFailureType } from '../shared/@types/OpenAmStage';
import { useOpenAmStage } from '../shared/services/useOpenAmStage';

export const SmsPhoneNumberStage = () => {
  const { translation } = useLanguage();
  const { isLoading, initParams } = useOpenAm();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');
  const phoneNumberInput = usePhoneNumberMask();
  const query = useQueryParams();

  const { loginWithPhoneNumber } = useOpenAmStage();
  const { isWebView } = useTheme();

  useEffect(() => {
    borgAnalytics.sendVirtualPageView({
      channel: getChannel(isWebView),
      virtualName: 'SMS Verification',
    });
  }, [isWebView]);

  const backToLogin = useMemo(() => {
    if (query.get('from') === LoginView.LoginForm) {
      return (
        <Button
          variant={'text-undecorated'}
          disabled={isLoading}
          onClick={() => window.history.back()}
          className="login-page__container__back-button"
        >
          {translation.loginRegister.login.phone.back}
        </Button>
      );
    }
  }, [query, isLoading, translation]);

  const stripPhoneNumber = (pn: string) => pn.replace(/ /g, '');

  const sendPhoneNumber = useCallback(async () => {
    try {
      await loginWithPhoneNumber(stripPhoneNumber(phoneNumber), failure => {
        if (
          failure.stagefailure === StageFailureType.SubscriptioncheckSubscription ||
          failure.stagefailure === StageFailureType.SmsInvalidPhoneNumber
        ) {
          borgAnalytics.sendLoginEvent({
            channel: getChannel(isWebView),
            virtualName: 'Login failed',
            method: 'sms',
            action: 'login_failed',
            result: 'true',
            reason: failure.stagefailure,
          });
          setError(translation.loginRegister.login.box.loginError.subscriptionCheckError(initParams?.faqLink || '#'));
        }
      });
    } catch (err) {
      if ((err as AxiosError).isAxiosError) {
        const axiosError = err as OpenAmAuthenticateError;
        if (axiosError.response?.data?.message === StageFailureType.SubscriptioncheckSubscription) {
          borgAnalytics.sendLoginEvent({
            channel: getChannel(isWebView),
            virtualName: 'Login failed',
            method: 'sms',
            action: 'login_failed',
            result: 'true',
            reason: axiosError.response.data.message,
          });
          setError(translation.loginRegister.login.box.loginError.subscriptionCheckError(initParams?.faqLink || '#'));
          return;
        }
      }
      borgAnalytics.sendLoginEvent({
        channel: getChannel(isWebView),
        virtualName: 'Login failed',
        method: 'sms',
        action: 'login_failed',
        result: 'true',
        reason: `${err}`,
      });
      setError(translation.loginRegister.login.box.loginError.general);
    }
  }, [phoneNumber, loginWithPhoneNumber, setError, translation, initParams, isWebView]);

  return (
    <>
      <h3>{translation.loginRegister.login.box.headerSMS}</h3>

      <div className={'login-page__phone-number-input-container'}>
        <Input
          label={translation.loginRegister.login.phone.phoneNumber}
          name={'phonenumber'}
          type={'text'}
          inputMode={'tel'}
          value={phoneNumber}
          onChange={e => {
            setPhoneNumber(e.target.value);
            setError('');
          }}
          disabled={isLoading}
          maxLength={11}
          onKeyUp={onEnter(sendPhoneNumber)}
          ref={phoneNumberInput}
          hasError={!!error}
        />
      </div>

      {error && (
        <div
          className="login-page__container__error"
          dangerouslySetInnerHTML={{ __html: error }}
        ></div>
      )}

      <Button
        variant={'contained'}
        onClick={sendPhoneNumber}
        disabled={isLoading || stripPhoneNumber(phoneNumber).length < 9}
      >
        {translation.loginRegister.login.phone.next}
      </Button>

      {backToLogin}
    </>
  );
};
